import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, KeyRound, Laptop, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ApiKeyForm } from "@/components/settings/ai-settings-card";
import { useOnboardingActions } from "@/hooks/queries/use-onboarding";
import type { KeyOwner } from "@/hooks/queries/use-ai-settings";

function errorText(error: unknown): string | null {
  if (!error) return null;
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

function Choice({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-white/[0.08] p-4 text-left">
      <div className="flex items-center gap-2 font-medium">
        {icon}
        {title}
      </div>
      <p className="text-sm text-muted-foreground">{description}</p>
      <div className="mt-auto flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

/**
 * Onboarding step: how will AI run this person's work? Their own computer, or
 * an API key (an admin can add one for everyone). Shown once, skippable, and
 * not shown at all to someone who already has a computer or a key.
 */
export function AiSetupStep({
  organizationName,
  isAdmin,
  onSkip,
  onKeySaved,
}: {
  organizationName: string;
  isAdmin: boolean;
  /** Replaces "Skip for now" finishing onboarding, e.g. to close a dialog. */
  onSkip?: () => void;
  /** Called after a key is saved, e.g. to go on to the work being started. */
  onKeySaved?: () => void;
}) {
  const navigate = useNavigate();
  const { completeAiSetup } = useOnboardingActions();
  const [keyOwner, setKeyOwner] = useState<KeyOwner | null>(null);

  const finish = async (then?: string) => {
    await completeAiSetup.mutateAsync();
    if (then) void navigate(then);
  };

  return (
    <div className="w-full space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">How will AI do your work?</h1>
        <p className="text-sm text-muted-foreground">
          Pick one to start. You can add the other any time in Settings → AI, and choose which to use each time you
          send work.
        </p>
      </div>

      {keyOwner ? (
        <ApiKeyForm
          owner={keyOwner}
          onDone={() => {
            setKeyOwner(null);
          }}
          onSaved={() => {
            void finish()
              .then(() => onKeySaved?.())
              .catch(() => undefined);
          }}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <Choice
            icon={<Laptop className="h-4 w-4" />}
            title="My computer"
            description="Runs on your own computer with the AI account you already use there, such as Claude Code. Nothing extra to pay."
          >
            <Button
              size="sm"
              disabled={completeAiSetup.isPending}
              onClick={() => void finish("/connect").catch(() => undefined)}
            >
              Connect my computer
            </Button>
          </Choice>
          <Choice
            icon={<KeyRound className="h-4 w-4" />}
            title="An API key"
            description={
              isAdmin
                ? `Paste a key from Anthropic, OpenAI, xAI or OpenRouter. Add it for everyone in ${organizationName} so nobody else has to set anything up. Requests run on it right away; planning and building need a computer for now.`
                : "Paste a key from Anthropic, OpenAI, xAI or OpenRouter. Requests run on it right away; planning and building need a computer for now."
            }
          >
            {isAdmin ? (
              <>
                <Button size="sm" onClick={() => { setKeyOwner("company"); }}>
                  <Building2 className="mr-1 h-3.5 w-3.5" />
                  Add for everyone
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setKeyOwner("personal"); }}>
                  Just for me
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => { setKeyOwner("personal"); }}>
                Add an API key
              </Button>
            )}
          </Choice>
        </div>
      )}

      {errorText(completeAiSetup.error) ? (
        <p className="text-center text-sm text-red-400" role="alert">
          {errorText(completeAiSetup.error)}
        </p>
      ) : null}

      <div className="flex justify-center">
        <Button
          variant="ghost"
          size="sm"
          disabled={completeAiSetup.isPending}
          onClick={() => {
            if (onSkip) onSkip();
            else void finish().catch(() => undefined);
          }}
        >
          {completeAiSetup.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Skip for now
        </Button>
      </div>
    </div>
  );
}
